import { EntityManager } from '../../../engine/ecs/core/EntityManager';
import { ComponentManager } from '../../../engine/ecs/core/ComponentManager';
import { Entity } from '../../../engine/ecs/core/Entity';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { MovementComponent } from '../../../engine/ecs/components/MovementComponent';
import {
  SpriteComponent,
  RenderingType,
} from '../../../engine/ecs/components/SpriteComponent';
import {
  CollisionComponent,
  CollisionShape,
} from '../../../engine/ecs/components/CollisionComponent';
import {
  AnimationComponent,
  AnimationState,
} from '../../../engine/ecs/components/AnimationComponent';
import { GhostComponent, GhostType } from '../components/GhostComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';

/**
 * GhostEntityFactory - Creates ghost AI entities
 */
export class GhostEntityFactory {
  private entityManager: EntityManager;
  private componentManager: ComponentManager;

  constructor() {
    this.entityManager = EntityManager.getInstance();
    this.componentManager = ComponentManager.getInstance();
  }

  /**
   * Create Blinky (Red Ghost) - Direct chaser
   */
  public createBlinky(x: number, y: number, speed?: number): Entity {
    return this.createGhost(
      x,
      y,
      GhostType.Blinky,
      '#FF0000', // Red color
      new Vector2(0, 0), // Top-left corner
      new Vector2(x, y), // Base position
      speed // Pass level-specific speed
    );
  }

  /**
   * Create Pinky (Pink Ghost) - Ambusher
   */
  public createPinky(x: number, y: number, speed?: number): Entity {
    return this.createGhost(
      x,
      y,
      GhostType.Pinky,
      '#FFB8FF', // Pink color
      new Vector2(672, 0), // Top-right corner
      new Vector2(x, y), // Base position
      speed // Pass level-specific speed
    );
  }

  /**
   * Create Inky (Cyan Ghost) - Complex behavior
   */
  public createInky(x: number, y: number, speed?: number): Entity {
    return this.createGhost(
      x,
      y,
      GhostType.Inky,
      '#00FFFF', // Cyan color
      new Vector2(672, 504), // Bottom-right corner
      new Vector2(x, y), // Base position
      speed // Pass level-specific speed
    );
  }

  /**
   * Create Clyde (Orange Ghost) - Scatter when close
   */
  public createClyde(x: number, y: number, speed?: number): Entity {
    return this.createGhost(
      x,
      y,
      GhostType.Clyde,
      '#FFB852', // Orange color
      new Vector2(0, 504), // Bottom-left corner
      new Vector2(x, y), // Base position
      speed // Pass level-specific speed
    );
  }

  /**
   * Create a ghost entity with specified properties
   */
  private createGhost(
    x: number,
    y: number,
    ghostType: GhostType,
    color: string,
    homeCorner: Vector2,
    basePosition: Vector2,
    speed?: number
  ): Entity {
    const entity = this.entityManager.createEntity();

    // Use provided speed or default to 80
    const ghostSpeed = speed || 80;

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Movement component
    const movement = new MovementComponent(
      ghostSpeed, // Use level-specific speed
      1.0, // no drag for ghosts
      new Vector2(0, 0), // initial velocity
      new Vector2(0, 0) // initial acceleration
    );
    this.componentManager.addComponent(entity, movement);

    // Sprite component
    const sprite = new SpriteComponent(
      color, // ghost color
      new Vector2(24, 24), // size to match cell size
      undefined, // no texture for now
      true, // visible
      1.0, // opacity
      2, // z-index (above dots)
      RenderingType.Ghost // Use generic Ghost rendering
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(18, 18), // collision size
      new Vector2(0, 0), // offset
      false, // not a trigger
      'ghost', // collision layer
      ['pacman', 'wall'] // what can collide with
    );
    this.componentManager.addComponent(entity, collision);

    // Animation component
    const animation = new AnimationComponent(
      AnimationState.Moving, // initial state
      0.3, // animation speed
      true // loop animations
    );
    // Remove texture references to use built-in shape rendering
    animation.addAnimation(AnimationState.Moving, []);
    animation.addAnimation(AnimationState.Frightened, []);
    animation.addAnimation(AnimationState.Dead, []);
    this.componentManager.addComponent(entity, animation);

    // Ghost-specific component
    const ghostComponent = new GhostComponent(
      ghostType,
      homeCorner,
      basePosition,
      ghostSpeed // Use level-specific speed
    );
    this.componentManager.addComponent(entity, ghostComponent);

    return entity;
  }
}
